import { useCallback } from 'react';
import { BiddingSettingValue, SettingValueUnit } from '../api';

type SetFieldValue = (field: string, value: any, shouldValidate?: boolean) => void;

const isEmptyValue = (value: unknown) => value === null || value === undefined || value === '';

const getDefaultUnit = (setting: BiddingSettingValue) =>
    setting.isPercentageDefault ? SettingValueUnit.Percentage : SettingValueUnit.Amount;

const getValueForUnit = (setting: BiddingSettingValue, unit: SettingValueUnit) =>
    unit === SettingValueUnit.Amount ? setting.amount : setting.percentage;

export default function useSettingValueUnitDefaults(setFieldValue: SetFieldValue) {
    const ensureUnitAndMaybeValue = useCallback(
        (
            fieldName: string,
            setting: BiddingSettingValue | undefined,
            unit: SettingValueUnit | '',
            currentRawValue: unknown
        ) => {
            if (!setting) {
                return;
            }

            if (!unit) {
                const defaultUnit = getDefaultUnit(setting);
                setFieldValue(`${fieldName}.unit`, defaultUnit);
                setFieldValue(`${fieldName}.value`, getValueForUnit(setting, defaultUnit));

                return;
            }

            if (isEmptyValue(currentRawValue)) {
                setFieldValue(`${fieldName}.value`, getValueForUnit(setting, unit));
            }
        },
        [setFieldValue]
    );

    const onUnitChanged = useCallback(
        (fieldName: string, setting: BiddingSettingValue | undefined, unit: SettingValueUnit) => {
            if (!setting) {
                return;
            }

            setFieldValue(`${fieldName}.value`, getValueForUnit(setting, unit));
        },
        [setFieldValue]
    );

    return { ensureUnitAndMaybeValue, onUnitChanged };
}
